import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { LINE_LABEL_MAX, type Line } from '@chatapp-inbox/shared';
import { renameLine } from '../../api';
import { lineName } from '../conversations';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { adminErrorMessage } from './error-message';
import { Field } from './Field';
import { bitrixOpenlinesKey } from './keys';
import { renamedText } from './lines-text';
import { applyLine } from './use-connection';
import forms from './forms.module.css';
import styles from './LinesSection.module.css';

/**
 * Dar um nome ao número. A Meta costuma dar o mesmo nome de exibição a todos os números do escritório, e assim não dá
 * para distingui-los na lista, nas conversas nem no Bitrix. O nome escolhido vale em todo o CladvApp e é o nome da linha
 * aberta do número no Bitrix (a que já existe também é renomeada). Em branco, volta ao nome do serviço.
 */
export function LineRename({ line }: { line: Line }): ReactElement {
  const queryClient = useQueryClient();
  const status = useStatusMessage();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const openButton = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  // Fechou o formulário: o foco volta ao botão que o abriu (quem usa teclado não perde o lugar).
  const closedNow = useRef(false);
  const name = lineName(line);

  useEffect(() => {
    if (editing) input.current?.focus();
    else if (closedNow.current) {
      closedNow.current = false;
      openButton.current?.focus();
    }
  }, [editing]);

  const close = (): void => {
    closedNow.current = true;
    setEditing(false);
    setError(null);
  };

  const save = useMutation({
    mutationFn: (label: string | null) => renameLine(line.id, label),
    onSuccess: ({ line: saved, bitrix }) => {
      applyLine(queryClient, saved);
      // O nome da linha aberta aparece também na seção do Canal Aberto do Bitrix.
      void queryClient.invalidateQueries({ queryKey: bitrixOpenlinesKey });
      status.announce(renamedText(saved, bitrix));
      close();
    },
    onError: (failure) => {
      setError(adminErrorMessage(failure, 'Não foi possível salvar o nome. Tente de novo.'));
      input.current?.focus();
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (save.isPending) return;
    setError(null);
    status.announce('');
    const next = value.replace(/\s+/g, ' ').trim();
    // Nada mudou: não há o que pedir ao servidor (nem ao Bitrix).
    if (next === (line.label ?? '')) {
      close();
      return;
    }
    save.mutate(next === '' ? null : next);
  };

  return (
    <div className={styles.rename}>
      {editing ? (
        <form onSubmit={submit} noValidate className={styles.renameForm}>
          <Field
            ref={input}
            label="Nome do número"
            hint="Aparece na lista de números, nas conversas e como nome da linha aberta no Bitrix. Em branco, volta ao nome que a Meta dá ao número."
            maxLength={LINE_LABEL_MAX}
            autoComplete="off"
            value={value}
            readOnly={save.isPending}
            onChange={(event) => {
              setValue(event.target.value);
              setError(null);
            }}
          />
          {error && (
            <p role="alert" className={forms.alert}>
              {error}
            </p>
          )}
          <div className={forms.actions}>
            <Button type="submit" variant="primary" busy={save.isPending}>
              {save.isPending ? 'Salvando…' : 'Salvar nome'}
            </Button>
            <Button onClick={close}>Cancelar</Button>
          </div>
        </form>
      ) : (
        <Button
          ref={openButton}
          aria-label={`Renomear o número ${name}`}
          onClick={() => {
            setValue(line.label ?? '');
            setError(null);
            status.announce('');
            setEditing(true);
          }}
        >
          Renomear
        </Button>
      )}
      <StatusRegion message={status.message} />
    </div>
  );
}
